import React from "react"
import { downloadBlob, formatBytes, formatDateLabel, getReportFileName } from "../../lib/utils"
import apiServerApi from "../../redux/sagas/apiServerApi"
import { blockingFetchService } from "../../lib/blockingFetch"
import FontAwesome from "react-fontawesome"

const statusConfig = {
  PENDING: { label: 'Pending', color: '#6b7280' },
  IN_PROGRESS: { label: 'In progress', color: '#b45309' },
  READY: { label: 'Ready', color: '#047857' },
  FAILED: { label: 'Failed', color: '#b91c1c' },
}

const ReportDisplayNames = {
  'Active Loans - Details(Pentaho)': 'Active Loans Details',
  'Active Loans - Summary(Pentaho)': 'Active Loans Summary',
  'Expected Payments By Date - Formatted': 'Expected Payments By Date',
  'Balance Outstanding': 'Balance Outstanding',
  'Aging Detail(Pentaho)': 'Aging Loans',
  'Portfolio at Risk(Pentaho)': 'Portfolio At Risk',
  'BranchCashInDetails': 'Branch Cash In Details',
  'BranchCashInSummary': 'Branch Cash In Summary',
  'ExpectedPaymentsByDateAndAffiliation': 'Expected Payments By Date',
  'ActiveLoansByDate': 'Active Loans',
  'FullyRepaidLoansByDate': 'Fully Repaid Loans',
  'ActiveLoansAndFullyRepaidLoansByDate': 'Active + Fully Repaid Loans',
  'GeneralLedgerReportAll': 'General Ledger Report',
  'Income Statement': 'Income Statement',
  'Balance Sheet': 'Balance Sheet',
  'Trial Balance': 'Trial Balance',
  'LoansDisbursedByAffiliation': 'Loans Disbursed By Affiliation',
  'BatchPerformanceDetails': 'Batch Performance Details',
  'BatchPerformanceSummary': 'Batch Performance Summary',
  'BucketPerformanceDetails': 'Bucket Performance Details',
  'BucketPerformanceSummary': 'Bucket Performance Summary',
  'DefaultedLoansDetails': 'Defaulted Loans',
}

const Spinner = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ animation: 'dr-spin 1s linear infinite' }}
  >
    <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="3" />
    <path
      d="M22 12a10 10 0 0 1-10 10"
      stroke="#2563eb"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
)

const StatusIcon = ({ status }) => {
  if (status === 'PENDING' || status === 'IN_PROGRESS') {
    return <Spinner />
  }
  if (status === 'READY') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14"
          stroke="#047857"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
  if (status === 'FAILED') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="#b91c1c" strokeWidth="2" />
        <path
          d="M12 8v5M12 16h.01"
          stroke="#b91c1c"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    )
  }
  return null
}

export default class DownloadRequestItem extends React.Component {

  constructor(props) {
    super(props)
    this.state = {
      downloading: false,
      deleting: false,
      error: null
    }
    this.handleDownload = this.handleDownload.bind(this)
    this.handleDelete = this.handleDelete.bind(this)
  }

  async handleDownload() {

    const { request } = this.props
    this.setState({ downloading: true })

    blockingFetchService(
      accessToken => apiServerApi.downloadReportRequest(accessToken, request.id),
      blob => {

        const fileName = getReportFileName(
          ReportDisplayNames[request.reportName] || request.reportName, 
          request.outputType, 
          request.updatedAt || request.createdAt
        )
        downloadBlob(blob, fileName)
      },
      error => {
        this.setState({ error: error.text })
        console.error('Unable to download report', error)
      },
      () => {
        this.setState({ downloading: false })
      }
    )
  }

  async handleDelete() {

    this.setState({ deleting: true })
    const { request: { id } } = this.props

    blockingFetchService(
      accessToken => apiServerApi.deleteDownloadRequest(accessToken, id),
      () => {
        this.props.removeDownloadRequest(id)
      },
      error => {
        this.setState({ error: error.text })
        console.error('Unable to delete download request', error)
      },
      () => {
        this.setState({ deleting: false })
      }
    )
  }

  render() {

    const { request } = this.props,
    { downloading, deleting, error } = this.state,
    cfg = statusConfig[request.status] || statusConfig.PENDING,
    isRunning = request.status === 'PENDING' || request.status === 'IN_PROGRESS',
    reportDisplayName = ReportDisplayNames[request.reportName] || request.reportName

    return (
      <div className="dr-item">
        <div className="dr-item-header">

          <div className="dr-item-icon">
            <StatusIcon status={ request.status } />
          </div>

          <div className="dr-item-main">
            <div className="dr-item-title" title={ reportDisplayName }>
              { reportDisplayName }
            </div>
            <div className="dr-item-meta">
              <span className="dr-badge" style={{ color: cfg.color }}>
                { cfg.label }
              </span>
              <span className="text-gray-400">·</span>
              <span>{ request.outputType }</span>
              <span className="text-gray-400">·</span>
              <span>{ formatBytes(request.size) }</span>
            </div>
            {error && <div className="dr-error">{ error }</div>}
            {request.status === 'FAILED' && request.errorMessage && (
              <div className="dr-error">{ request.errorMessage }</div>
            )}
            <div className="dr-date" title={ new Date(request.updatedAt || request.createdAt).toLocaleString() }>
              { formatDateLabel(new Date(request.updatedAt || request.createdAt)) }
            </div>
          </div>

          <button
            className="rounded-full border-none py-8 px-12 bg-gray-50 hover:bg-gray-200 text-gray-500 hover:text-gray-600"
            onClick={ this.handleDelete }
            disabled={ downloading || deleting || isRunning }
            title="Delete"
            aria-label="Delete"
          >
            <FontAwesome name="trash" className='text-sm' />
          </button>
        </div>
        {request.status === 'READY' && (
          <button
            className="dr-download-btn"
            onClick={ this.handleDownload }
            disabled={ downloading || deleting }
          >
            { downloading ? 'Downloading…' : 'Download' }
          </button>
        )}
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/DownloadRequests/DownloadRequestItem.js